import { useEffect, useState } from "react";
import {
  Pressable,
  ActivityIndicator,
  View,
} from "react-native";
import styled from "styled-components/native";
import { MemberListSkeleton } from "@/components/Skeleton";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { useChannels } from "@/hooks/useChannels";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { colors, radius, font } from "@/lib/theme";
import type { ChannelMember } from "@/lib/types";
import ContentWidth from "@/components/ContentWidth";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

/* ── Types ── */

interface PendingRequest extends ChannelMember {
  id: string; // doc id (userId)
  channelId: string;
  channelName: string;
  email?: string;
}

/* ── Styled Components ── */

const Screen = styled.ScrollView`
  flex: 1;
  background-color: ${colors.bg};
`;

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 8px;
`;

const HeaderLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CountBadge = styled.View`
  background-color: ${colors.amber};
  border-radius: ${radius.full}px;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding-horizontal: 6px;
`;

const CountBadgeText = styled.Text`
  color: #fff;
  font-size: 11px;
  font-weight: ${font.extrabold};
`;

const SectionHeader = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding-horizontal: 20px;
  padding-top: 20px;
  padding-bottom: 8px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Card = styled.View`
  margin-horizontal: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const EmptyCard = styled.View`
  margin-horizontal: 20px;
  margin-top: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 32px;
  align-items: center;
`;

const EmptyEmoji = styled.Text`
  font-size: 36px;
  margin-bottom: 12px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  ${(p) =>
    p.bordered
      ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};`
      : ""}
`;

const PendingAvatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.amber50};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const PendingAvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.amber};
`;

const Flex1 = styled.View`
  flex: 1;
`;

const MemberName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MemberSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const ChannelBadge = styled.View`
  background-color: ${colors.brand50};
  border-radius: ${radius.full}px;
  padding-horizontal: 8px;
  padding-vertical: 2px;
  margin-top: 4px;
  align-self: flex-start;
`;

const ChannelBadgeText = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
`;

const Actions = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ApproveBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding-horizontal: 12px;
  padding-vertical: 6px;
`;

const ApproveBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const RejectBtnWrap = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.sm}px;
  padding-horizontal: 12px;
  padding-vertical: 6px;
  border-width: 1px;
  border-color: ${colors.danger};
`;

const RejectBtnText = styled.Text`
  color: ${colors.danger};
  font-size: 12px;
  font-weight: ${font.bold};
`;

const Spacer = styled.View`
  height: 32px;
`;

/* ── Screen ── */

export default function ChannelRequestsScreen() {
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { channels, loading: channelsLoading } = useChannels(
    club?.id,
    user?.uid
  );

  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { alertProps, alert } = useAlert();
  const [requests, setRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  const canManage =
    membership?.role === "admin" || isAnyCaptain(user?.uid);

  // Listen to pending members across all channels
  useEffect(() => {
    if (!club?.id || !channels.length) {
      setRequests([]);
      setLoading(false);
      return;
    }

    const unsubscribers: (() => void)[] = [];
    const allRequests = new Map<string, PendingRequest[]>();

    channels.forEach((channel) => {
      const q = query(
        collection(
          db,
          "clubs",
          club.id,
          "channels",
          channel.id,
          "members"
        ),
        where("status", "==", "pending")
      );

      const unsub = onSnapshot(q, (snap) => {
        const channelRequests: PendingRequest[] = snap.docs.map((d) => ({
          id: d.id,
          channelId: channel.id,
          channelName: channel.name,
          ...(d.data() as ChannelMember),
        }));

        allRequests.set(channel.id, channelRequests);

        // Flatten all channel requests
        const flat: PendingRequest[] = [];
        allRequests.forEach((reqs) => flat.push(...reqs));
        setRequests(flat);
        setLoading(false);
      });

      unsubscribers.push(unsub);
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [club?.id, channels.length]);

  const handleApprove = async (req: PendingRequest) => {
    if (!club?.id) return;
    const key = `${req.channelId}-${req.id}`;
    setProcessing(key);
    try {
      await updateDoc(
        doc(
          db,
          "clubs",
          club.id,
          "channels",
          req.channelId,
          "members",
          req.id
        ),
        { status: "active" }
      );
    } catch (err: any) {
      const msg = err.message || "Failed to approve request.";
      await alert({ title: "Error", emoji: "😕", message: msg, buttonColor: "#dc2626" });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (req: PendingRequest) => {
    if (!club?.id) return;
    const key = `${req.channelId}-${req.id}`;
    setProcessing(key);
    try {
      await deleteDoc(
        doc(
          db,
          "clubs",
          club.id,
          "channels",
          req.channelId,
          "members",
          req.id
        )
      );
    } catch (err: any) {
      const msg = err.message || "Failed to reject request.";
      await alert({ title: "Error", emoji: "😕", message: msg, buttonColor: "#dc2626" });
    } finally {
      setProcessing(null);
    }
  };

  if (loading || channelsLoading) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.bg }}>
        <MemberListSkeleton />
      </View>
    );
  }

  if (!canManage) {
    return (
      <Center>
        <EmptyText>Only admins and captains can manage requests.</EmptyText>
      </Center>
    );
  }

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Header */}
        <Header>
          <HeaderLabel>Channel Requests</HeaderLabel>
          {requests.length > 0 && (
            <CountBadge>
              <CountBadgeText>{requests.length}</CountBadgeText>
            </CountBadge>
          )}
        </Header>

        {requests.length > 0 ? (
          <>
            <SectionHeader>
              <SectionLabel>Pending</SectionLabel>
              <CountBadge>
                <CountBadgeText>{requests.length}</CountBadgeText>
              </CountBadge>
            </SectionHeader>
            <Card>
              {requests.map((req, i) => {
                const key = `${req.channelId}-${req.id}`;
                const isProcessing = processing === key;
                return (
                  <Row key={key} bordered={i < requests.length - 1}>
                    <PendingAvatar>
                      <PendingAvatarText>
                        {req.displayName?.[0]?.toUpperCase() || "?"}
                      </PendingAvatarText>
                    </PendingAvatar>
                    <Flex1>
                      <MemberName>{req.displayName}</MemberName>
                      {req.email ? (
                        <MemberSub>{req.email}</MemberSub>
                      ) : null}
                      <ChannelBadge>
                        <ChannelBadgeText>
                          {req.channelName}
                        </ChannelBadgeText>
                      </ChannelBadge>
                    </Flex1>
                    <Actions>
                      {isProcessing ? (
                        <ActivityIndicator
                          size="small"
                          color={colors.brand}
                        />
                      ) : (
                        <>
                          <Pressable
                            style={({ pressed }) =>
                              pressed ? { opacity: 0.7 } : {}
                            }
                            onPress={() => handleApprove(req)}
                          >
                            <ApproveBtnWrap>
                              <ApproveBtnText>Approve</ApproveBtnText>
                            </ApproveBtnWrap>
                          </Pressable>
                          <Pressable
                            style={({ pressed }) =>
                              pressed ? { opacity: 0.7 } : {}
                            }
                            onPress={() => handleReject(req)}
                          >
                            <RejectBtnWrap>
                              <RejectBtnText>Reject</RejectBtnText>
                            </RejectBtnWrap>
                          </Pressable>
                        </>
                      )}
                    </Actions>
                  </Row>
                );
              })}
            </Card>
          </>
        ) : (
          <EmptyCard>
            <EmptyEmoji>{"\u2705"}</EmptyEmoji>
            <EmptyText>No pending channel requests.</EmptyText>
          </EmptyCard>
        )}

        <Spacer />
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </Screen>
  );
}
